import {
  Anchor,
  Group,
  Text,
  Space,
  Box,
  Image,
  Divider,
  useMantineTheme,
  Button,
  Grid,
  Col,
  Tooltip,
} from "@mantine/core";
import Icon from "./Components/Icon";
import { ROUTER_BASE } from "./Components/Link";
import { useElementSize } from "@mantine/hooks";
import { FooterLogo } from "./Components/Logo";

function Footer({
  chain,
  left = 0,
  isSmallScreen,
}: {
  chain: string;
  left?: number;
  isSmallScreen: boolean;
}) {
  const size = useElementSize();

  const theme = useMantineTheme();

  const dark = theme.colorScheme === "dark";

  const linkData1 = [
    {
      icon: (
        <Image
          width={16}
          height={16}
          src={`/images/eigentx/analysisTools.png`}
          alt=""
        />
      ),
      label: "EigenTx",
      tooltip: "chrome-extension",
      href: `https://chrome.google.com/webstore/detail/eigentx/gmjkhhheaknfaekapfiedhohdilpmgci?hl=zh-CN&gl=SG`,
    },
    {
      icon: <Icon name="icon-alert" size={14} />,
      label: "Alert",
      tooltip: "twitter@EigenPhi_Alert",
      href: `https://twitter.com/eigenphi_alert`,
      gradientColor: "linear-gradient(90deg,#ff58d0, #64adff)",
    },
    // {
    //   label: "Bounties",
    //   component: (
    //     <BountiesButton
    //       trigger={isSmallScreen ? "click" : "hover"}
    //       onClick={handleClick}
    //     />
    //   ),
    // },
    {
      icon: <Icon name="icon-Reports" size={14} />,
      label: "Reports",
      href: `/${ROUTER_BASE}/${chain}/dailyReport`,
    },
    {
      icon: <Icon name="icon-Classroom" size={14} />,
      label: "Classroom",
      href: "https://eigenphi-1.gitbook.io/classroom/",
    },
    {
      icon: <Icon name="icon-Newsletter" size={14} />,
      label: "Newsletter",
      href: "https://eigenphi.substack.com/",
    },
    {
      icon: <Icon name="icon-UserGuide" size={14} />,
      label: "User Guide",
      href: "https://eigenphi-1.gitbook.io/arbitrage-scan-user-guide/",
    },
  ].filter(Boolean);

  const linkData2 = [
    {
      icon: <Icon name="icon-twitter" size={14} />,
      label: "Twitter",
      showLabel: true,
      href: `https://twitter.com/eigenphi`,
      buttonColor: "#1D9BF0",
    },
    {
      icon: <Icon name="icon-discord" size={14} />,
      label: "Discord",
      showLabel: true,
      href: `https://discord.gg/2B96UzwpHB`,
      buttonColor: "#427AF7",
    },
    {
      icon: <Icon name="icon-medium" size={12} />,
      label: "Medium",
      href: `https://medium.com/@eigenphi/`,
      buttonColor: dark ? "#373A40" : "#E9ECEF",
    },
    {
      icon: <Icon name="icon-mirror" size={12} />,
      label: "Mirror",
      href: `https://mirror.xyz/0xc19565163aFdEe3783FC970E4Bd0275B11848d34`,
      type: "light",
      buttonColor: dark ? "#373A40" : "#E9ECEF",
    },
    {
      icon: <Icon name="icon-mail-fill" size={13} />,
      label: "Contact Us",
      href: `mailto:contact@eigenphi.com`,
      type: "light",
      buttonColor: dark ? "#373A40" : "#E9ECEF",
    },
  ];

  const renderOurLinks = () => {
    const renderButton = (item: any) => {
      if (item.component) return item.component;

      return (
        <Anchor href={item.href} target="_blank">
          <Button
            size="sm"
            variant="subtle"
            sx={{
              fontSize: 14,
              fontWeight: 400,
              color: theme.colorScheme === "dark" ? "#B5B5C3" : "#868E96",
              "&:hover": {
                backgroundColor: "rgba(68,215,182,0.10) !important",
                color: dark ? "#44D7B6" : "#54B498",
              },
            }}
            // onClick={() => handleClick(item.href)}
          >
            <Group noWrap spacing={6}>
              <Box
                sx={
                  item.gradientColor
                    ? {
                        fontSize: 14,
                        fontWeight: 400,
                        background: item.gradientColor,
                        backgroundClip: "text",
                        color: "rgba(255, 255, 255, 0)",
                      }
                    : {}
                }
              >
                {item.icon}
              </Box>
              {item.label}
            </Group>
          </Button>
        </Anchor>
      );
    };

    return isSmallScreen ? (
      <Grid>
        {linkData1.map((item, idx) => {
          return (
            <Col span={6} key={idx}>
              {renderButton(item)}
            </Col>
          );
        })}
      </Grid>
    ) : (
      <Group spacing={0} align="flex-start">
        {linkData1.map((item, idx) => {
          const wrap = <Box key={idx}>{renderButton(item)}</Box>;

          return item?.tooltip ? (
            <Tooltip key={idx} withArrow label={item?.tooltip}>
              {wrap}
            </Tooltip>
          ) : (
            wrap
          );
        })}
      </Group>
    );
  };

  const thirdPartySocials = (
    <Group spacing={6}>
      {linkData2.map((item, idx) => {
        return (
          <Anchor key={idx} href={item.href} target="_blank">
            <Button
              sx={{
                padding: "0 2px",
                fontWeight: 400,
                background: item.buttonColor,
                color: item.showLabel ? "#FFFFFF" : "#868E96",
                "&:hover": {
                  background: item.buttonColor,
                },
              }}
            >
              <Group noWrap spacing={0}>
                <Box sx={{ width: 30, textAlign: "center" }}>{item.icon}</Box>
                {item.showLabel ? (
                  <Box sx={{ marginRight: 8 }}>{item.label}</Box>
                ) : (
                  ""
                )}
              </Group>
            </Button>
          </Anchor>
        );
      })}
    </Group>
  );

  const paddingTop = 42;
  const paddingBottom = 28;

  const date = new Date();
  const fullYear = date.getFullYear(); //获取完整的年份(4位)

  return (
    <>
      <Box
        sx={{ height: size.height + paddingTop + paddingBottom, zIndex: -1 }}
      />
      <Box
        ref={size.ref}
        sx={(theme) => ({
          padding: isSmallScreen
            ? `${paddingTop}px 20px ${paddingBottom}px`
            : `${paddingTop}px 42px ${paddingBottom}px`,
          backgroundColor: dark ? theme.colors.dark[7] : "#FFFFFF",
          position: "fixed",
          bottom: 0,
          left,
          right: 0,
          zIndex: 1,
        })}
      >
        {isSmallScreen ? (
          <Group align="flex-start" sx={{ flexDirection: "column" }}>
            <Box sx={{ marginBottom: 10 }}>
              <FooterLogo dark={dark} />
            </Box>
            <Space h={10} />
            {renderOurLinks()}
            {thirdPartySocials}
          </Group>
        ) : (
          <Group position="apart" align="flex-start" noWrap>
            <Box sx={{ marginBottom: 10 }}>
              <FooterLogo dark={dark} />
            </Box>
            {renderOurLinks()}
            {thirdPartySocials}
          </Group>
        )}
        <Space h={25} />
        <Divider my={0} color={dark ? "#25262B" : "#E9ECEF"} />
        <Space h={20} />
        <Group position="apart">
          <Group spacing={24}>
            <Anchor
              href="https://www.eigenphi.com/terms-and-conditions"
              target="_blank"
              size="sm"
              sx={{ color: "#868E96" }}
            >
              Terms and Conditions
            </Anchor>
            <Anchor
              href="https://eigenphi.notion.site/Change-Log-5236a1642aed4f03a2bc7c9ce2b3c505"
              target="_blank"
              size="sm"
              sx={{ color: "#868E96" }}
            >
              Changelog
            </Anchor>
          </Group>
          <Text size="sm" sx={{ color: "#868E96" }}>
            Copyright © {fullYear} EigenPhi
          </Text>
        </Group>
      </Box>
    </>
  );
}

export default Footer;
